import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, MapPin, Briefcase, Calendar, Clock, ChevronDown, ChevronUp, Edit, Trash2, Power, DollarSign, Award, Share2, Linkedin, LinkIcon, Check } from 'lucide-react';

const JobCard = ({ job, onApply, onViewDetails, isAdmin, onEdit, onDelete, onToggleStatus }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const isInactive = job.status === 'inactive';
  const jobUrl = `${window.location.origin}/careers/${job.id}`;
  const postDate = new Date(job.created_at).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className={`w-full bg-white border ${isInactive ? 'border-slate-100 opacity-80' : 'border-slate-200 hover:border-blue-400'} rounded-2xl overflow-hidden transition-all duration-300 shadow-sm hover:shadow-xl`}
    >
      <div className="p-4 md:p-5">
        <div className="flex flex-col lg:flex-row justify-between lg:items-center gap-4">
          <div className="space-y-3 flex-1">
            <div className="flex items-center gap-2.5">
              <h3 className={`text-[17px] font-black tracking-tight ${isInactive ? 'text-slate-400' : 'text-primary'}`}>
                {job.title}
              </h3>
              {job.job_code && (
                <span className="text-tiny font-black text-slate-400 bg-slate-50 px-2 py-1 rounded border border-slate-100 uppercase tracking-tighter">
                   Code: {job.job_code}
                </span>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-y-2 gap-x-5 text-tiny font-bold text-slate-500 uppercase tracking-tight">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-blue-500" />
                {job.location}
              </div>
              <div className="flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-blue-500" />
                {job.department}
              </div>
              {job.work_model && (
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-blue-500" />
                  {job.work_model}
                </div>
              )}
              {job.experience && (
                <div className="flex items-center gap-2">
                  <Award className="w-4 h-4 text-blue-500" />
                  {job.experience}
                </div>
              )}
              {job.salary && (
                <div className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-blue-500" />
                  {job.salary}
                </div>
              )}
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-blue-500" />
                {postDate}
              </div>
            </div>

            {job.tags && (
              <div className="flex flex-wrap gap-2 pt-1">
                {job.tags.split(',').map((tag, idx) => (
                  <span key={idx} className="px-2 py-0.5 bg-blue-50 text-blue-600 text-tiny font-black uppercase tracking-wider rounded-md border border-blue-100/50">
                    {tag.trim()}
                  </span>
                ))}
              </div>
            )}

            {!isExpanded && (
              <>
                <p className="text-slate-600 text-subtext line-clamp-1 max-w-2xl font-medium">
                  {job.description}
                </p>
                {!isAdmin && onViewDetails && (
                  <button
                    onClick={() => onViewDetails(job)}
                    className="text-primary text-subtext font-black hover:underline"
                  >
                    More
                  </button>
                )}
              </>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {isAdmin ? (
              <div className="flex items-center gap-2">
                <button
                  onClick={onEdit}
                  className="p-2.5 bg-slate-50 hover:bg-blue-50 text-slate-600 hover:text-blue-600 rounded-lg transition-all border border-slate-100"
                  title="Edit Job"
                >
                  <Edit className="w-4.5 h-4.5" />
                </button>
                <button
                  onClick={() => onToggleStatus(job)}
                  className={`p-2.5 rounded-lg transition-all border ${isInactive ? 'bg-green-50 text-green-600 border-green-100 hover:bg-green-100' : 'bg-orange-50 text-orange-600 border-orange-100 hover:bg-orange-100'}`}
                  title={isInactive ? "Set to Active" : "Set to Inactive"}
                >
                  <Power className="w-4.5 h-4.5" />
                </button>
                <button
                  onClick={onDelete}
                  className="p-2.5 bg-red-50 hover:bg-red-100 text-red-500 rounded-lg transition-all border border-red-100"
                  title="Delete Job"
                >
                  <Trash2 className="w-4.5 h-4.5" />
                </button>
              </div>
            ) : (
              <div className="flex gap-2.5 items-center">
                <button
                  disabled={isInactive || !onApply}
                  onClick={() => onApply && onApply(job)}
                  className={`px-6 py-2.5 rounded-xl font-black text-tiny transition-all shadow-md ${
                    isInactive
                    ? 'bg-slate-100 text-slate-400 cursor-not-allowed shadow-none'
                    : 'bg-primary text-white hover:bg-secondary active:scale-95'
                  }`}
                >
                  {isInactive ? 'Applications Closed' : 'Apply Now'}
                </button>
                {onViewDetails && (
                  <button
                    onClick={() => onViewDetails(job)}
                    className="px-5 py-2.5 rounded-xl font-black text-tiny border border-slate-200 text-primary bg-white hover:bg-slate-50 transition-all"
                  >
                    More
                  </button>
                )}
              </div>
            )}
            
            <div className="relative">
              <button
                onClick={() => setShowShareMenu(!showShareMenu)}
                className="p-2.5 hover:bg-slate-50 text-slate-400 hover:text-primary rounded-lg transition-all"
                title="Share Job"
              >
                <Share2 className="w-4.5 h-4.5" />
              </button>
              {showShareMenu && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowShareMenu(false)} />
                  <div className="absolute right-0 top-10 z-20 bg-white rounded-xl shadow-xl border border-slate-200 py-2 w-52">
                    <a
                      href={(() => {
                        const lines = [
                          `We're hiring! ${job.title} at Libsys`,
                          '',
                          job.location ? `Location: ${job.location}` : '',
                          job.type ? `Type: ${job.type}` : '',
                          job.work_model ? `Work Model: ${job.work_model}` : '',
                          job.experience ? `Experience: ${job.experience}` : '',
                          '',
                          `Apply here: ${jobUrl}`,
                          '',
                          '#hiring #jobs #careers'
                        ].filter(Boolean).join('\n');
                        return `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(lines)}`;
                      })()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 transition-colors text-sm font-bold text-slate-700"
                      onClick={() => setShowShareMenu(false)}
                    >
                      <Linkedin className="w-4 h-4 text-[#0A66C2]" />
                      Share on LinkedIn
                    </a>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(jobUrl);
                        setLinkCopied(true);
                        setShowShareMenu(false);
                        setTimeout(() => setLinkCopied(false), 2000);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 transition-colors text-sm font-bold text-slate-700"
                    >
                      {linkCopied ? <Check className="w-4 h-4 text-emerald-500" /> : <LinkIcon className="w-4 h-4 text-slate-400" />}
                      {linkCopied ? 'Link Copied!' : 'Copy Link'}
                    </button>
                  </div>
                </>
              )}
            </div>

            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-2.5 hover:bg-slate-50 text-slate-400 hover:text-primary rounded-lg transition-all group/expand"
            >
              {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5 group-hover/expand:translate-y-0.5 transition-transform" />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="pt-8 mt-8 border-t border-slate-100 space-y-6">
                <div className="flex flex-col lg:flex-row gap-8">
                  {/* Main Content */}
                  <div className="lg:w-2/3 space-y-8">
                    {onApply && (
                      <div className="flex flex-wrap gap-4 border-b border-slate-100 pb-8">
                        <button
                          onClick={() => onApply(job)}
                          className="px-8 py-3 bg-blue-600 text-white font-black rounded-xl hover:bg-blue-700 transition-all flex items-center gap-2 shadow-lg shadow-blue-500/20"
                        >
                          Apply Now <ArrowRight className="w-4 h-4" />
                        </button>
                      </div>
                    )}

                    <div className="prose prose-slate max-w-none">
                      <h4 className="text-h4 font-black text-primary uppercase tracking-widest mb-4">Job Description</h4>
                      <p className="text-slate-600 text-body whitespace-pre-wrap">
                        {job.description}
                      </p>
                    </div>

                    {job.responsibilities && (
                      <div className="prose prose-slate max-w-none">
                        <h4 className="text-h4 font-black text-primary uppercase tracking-widest mb-4">Key Responsibilities</h4>
                        <div className="text-slate-600 text-body leading-relaxed font-medium whitespace-pre-wrap">
                          {job.responsibilities}
                        </div>
                      </div>
                    )}

                    {job.skills && (
                      <div className="prose prose-slate max-w-none">
                        <h4 className="text-h4 font-black text-primary uppercase tracking-widest mb-4">Must-Have Skills</h4>
                        <div className="text-slate-600 text-body leading-relaxed font-medium whitespace-pre-wrap">
                          {job.skills}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Sidebar Metadata */}
                  <div className="lg:w-1/3 bg-slate-50/50 rounded-2xl p-6 border border-slate-100 space-y-6 self-start">
                    <div className="space-y-4">
                      {job.job_code && (
                        <div>
                          <label className="text-tiny font-black text-slate-400 uppercase tracking-widest block mb-1">Job Code</label>
                          <p className="text-subtext font-black text-primary">{job.job_code}</p>
                        </div>
                      )}
                      {job.manager && (
                        <div>
                          <label className="text-tiny font-black text-slate-400 uppercase tracking-widest block mb-1">Recruitment Manager</label>
                          <p className="text-subtext font-bold text-slate-600">{job.manager}</p>
                        </div>
                      )}
                      {job.experience_level && (
                        <div>
                          <label className="text-tiny font-black text-slate-400 uppercase tracking-widest block mb-1">Experience Level</label>
                          <p className="text-subtext font-bold text-slate-600">{job.experience_level}</p>
                        </div>
                      )}
                      {job.experience && (
                        <div>
                          <label className="text-tiny font-black text-slate-400 uppercase tracking-widest block mb-1">Years of Experience</label>
                          <p className="text-subtext font-bold text-slate-600">{job.experience}</p>
                        </div>
                      )}
                      {job.work_auth && (
                        <div>
                          <label className="text-tiny font-black text-slate-400 uppercase tracking-widest block mb-1">Work Authorization</label>
                          <p className="text-subtext font-bold text-slate-600">{job.work_auth}</p>
                        </div>
                      )}
                      <div>
                        <label className="text-tiny font-black text-slate-400 uppercase tracking-widest block mb-1">Job Status</label>
                        <p className={`text-subtext font-bold ${isInactive ? 'text-red-500' : 'text-green-600'}`}>
                          {isInactive ? 'Inactive' : 'Active'}
                        </p>
                      </div>
                      {job.salary && (
                        <div>
                          <label className="text-tiny font-black text-slate-400 uppercase tracking-widest block mb-1">Pay Rate / Salary</label>
                          <p className="text-subtext font-bold text-slate-600">{job.salary}</p>
                        </div>
                      )}
                    </div>

                    {job.tags && (
                      <div className="pt-4 border-t border-slate-200">
                        <label className="text-tiny font-black text-slate-400 uppercase tracking-widest block mb-3">Primary Skills</label>
                        <div className="flex flex-wrap gap-2">
                          {job.tags.split(',').map((tag, idx) => (
                            <span key={idx} className="px-2 py-1 bg-white text-slate-600 text-tiny font-bold rounded-lg border border-slate-200 shadow-sm">
                              {tag.trim()}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default JobCard;
